import { Filmomtale, Genre } from "@filmweb/sanity-types";
import SNoPoster from "components/NoPoster";
import CMSImage from "components/images/Image";
import SFacts from "components/program/SFacts";
import SKinoklubbTicket from "components/program/SKinoklubbTicket";
import SShowsContainer from "components/program/SShowsContainer";
import STicketButton from "components/program/STicketButton";
import { toRem } from "lib/client/fonts";
import isKinoklubb from "lib/isKinoklubb";
import { IFilm } from "lib/types/Program";
import { IMovieArticle, getFilmUrlFromEdi } from "lib/types/sanity";
import Link from "next/link";
import styled, { css } from "styled-components";

//#region [Props]
type SPosterContainerProps = {
	$isKinoklubb?: boolean;
}
//#endregion

//#region [Styles]
const SMovie = styled.article`
	display: flex;
	margin-bottom: 40px;
	gap: 20px;
	padding: 0 10px;

	@media screen and (min-width: 641px) {
		margin-bottom: 60px;
		gap: 30px;
	}
`;

const STextContainer = styled.div`
	flex: 1 1 auto;
`;

const SMovieTitle = styled.h1`
	margin: 0;
	line-height: 1.4;
	font-size: ${toRem(25)};
	@media screen and (min-width: 641px) {
		font-size: ${toRem(31)};
	}

`;

const SPosterContainer = styled.a<SPosterContainerProps>`
	display: block;
	position: relative;
	max-width: 100px;

	@media screen and (min-width: 641px) {
		max-width: 150px;
	}

	flex: 1 1 auto;
	width: 100%;
	${props => props.$isKinoklubb && css`
		&::after {
			content: "";
			display: block;
			position: absolute;
			z-index: 1;
			top: 0;
			right: 0;
			width: 50%;
			height: 0;
			padding-bottom: 50%;
			background-image: url(/KinoklubbRibbon.svg);
			background-repeat: no-repeat;
		}
	`}

	&:hover + ${STextContainer} ${SMovieTitle} {
		text-decoration: underline;
	}

`;

const SPoster = styled(CMSImage)`
	height: auto;
	max-width: 100px;
	width: 100%;
	display: block;

	@media screen and (min-width: 641px) {
		max-width: 150px;
	}
`;

//#endregion

//#region [Props]
type MovieProps = {
	movie: IFilm;
	filmwebMovie?: Filmomtale;
	allowTitleOverride?: boolean;
	localMovie?: IMovieArticle;
};
//#endregion

//#region [Component]
export default function MoviePosterRow({ movie, filmwebMovie, allowTitleOverride, localMovie }: MovieProps) {
	const poster = filmwebMovie?.mainPosterV2 ?? filmwebMovie?.postersV2?.[0];
	let title: string = movie.title;
	if (allowTitleOverride && localMovie?.titleOverride) {
		title = localMovie.titleOverride;
	}

	return <SMovie>
		{!filmwebMovie && <p>Mangler filmomtale fra Filmweb! ({movie.mainVersionId} - {movie.title})</p>}
		{filmwebMovie && <>
			<Link href={getFilmUrlFromEdi(movie.mainVersionId)} passHref legacyBehavior>
				<SPosterContainer $isKinoklubb={isKinoklubb(filmwebMovie)} aria-label={movie.title}>
					{poster && <SPoster image={poster} isFilmweb width={150} altText={`Plakat for '${filmwebMovie.title}'`} />}
					{!poster && <SNoPoster />}
				</SPosterContainer>
			</Link>
			<STextContainer>
				<Link href={getFilmUrlFromEdi(movie.mainVersionId)}>
					<SMovieTitle>{title}</SMovieTitle>
				</Link>
				<SFacts>
					{(filmwebMovie?.genres?.length ?? 0) !== 0 && <li className="genre" title="Sjangre">{filmwebMovie?.genres?.map(g => (g as Genre).name).join(" / ")}</li>}
					{filmwebMovie?.runningTime && <li title="Spilletid">{filmwebMovie?.runningTime}</li>}
					{filmwebMovie?.ageRating?.age && <li title="Aldersgrense">{filmwebMovie?.ageRating?.age}</li>}
				</SFacts>
				<SShowsContainer>
					{movie.shows?.map(show => <STicketButton key={`${show.movieMainVersionId}|${show.showStart}`} show={show} showPassed showDate appendShowType />)}
					{isKinoklubb(filmwebMovie) && <SKinoklubbTicket />}
				</SShowsContainer>
			</STextContainer>
		</>}
	</SMovie>
}

//#endregion